import { WeatherStationEntityEntry } from '../domain/model/weatherStation.js';

// Service Interface
export interface WeatherStationDataService {
    fetchData(params?: WeatherStationQueryParams): Promise<WeatherStationResponse>;
    createStation(data: CreateWeatherStationRequest): Promise<WeatherStationEntityEntry>;
    updateStation(id: string, data: UpdateWeatherStationRequest): Promise<WeatherStationEntityEntry>;
    deleteStation(id: string): Promise<void>;
    getStationById(id: string): Promise<WeatherStationEntityEntry>;
}

// Request/Response Types
export interface WeatherStationQueryParams {
    page?: number;
    limit?: number;
    search?: string;
    region?: string;
    status?: string;
    cffdrsMode?: string;
    nesdis?: string;
    name?: string;
    contact?: string;
    elevation?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
}

export interface WeatherStationResponse {
    data: WeatherStationEntityEntry[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface CreateWeatherStationRequest {
    nesdis: string;
    name: string;
    region: string;
    geometry: {
        type: 'Point';
        coordinates: [number, number];
    };
    wkt: string;
    elevationMeters?: number;
    status: 'Fully Operational' | 'Demobilized' | 'Maintenance';
    xmitTime?: Date;
    contact?: string;
    sensorSuite?: string;
    sensorTokens?: string[];
    sensorArray?: string[];
    cffdrsMode?: 'off' | 'spring' | 'summer';
    cffdrsMeta?: Record<string, any>;
    fuelType?: string;
}

export interface UpdateWeatherStationRequest extends Partial<CreateWeatherStationRequest> { }

// Data Transformation Types
export interface WeatherStationDisplayData {
    id: string;
    displayName: string;
    regionCode: string;
    coordinates: string;
    statusIndicator: string;
    elevationDisplay: string;
    sensorDescription: string;
    lastTransmission: string;
    contactDisplay: string;
}

export interface WeatherStationExportData {
    nesdis: string;
    name: string;
    region: string;
    latitude: number;
    longitude: number;
    elevation: string;
    status: string;
    contact: string;
    sensors: string;
    cffdrsMode: string;
    fuelType: string;
}

// Error Handling Types
export interface WeatherStationError {
    code: string;
    message: string;
    field?: string;
    details?: Record<string, any>;
}

export interface WeatherStationValidationError extends WeatherStationError {
    field: string;
    validationRule: string;
}

export type WeatherStationServiceError = {
    type: 'VALIDATION_ERROR' | 'NETWORK_ERROR' | 'SERVER_ERROR' | 'NOT_FOUND' | 'UNAUTHORIZED';
    message: string;
    errors?: WeatherStationError[];
    statusCode?: number;
};

// Validation Schemas
export interface WeatherStationValidationSchema {
    nesdis: {
        required: true;
        pattern: RegExp;
        message: string;
    };
    name: {
        required: true;
        minLength: number;
        maxLength: number;
    };
    region: {
        required: true;
        allowedValues: string[];
    };
    coordinates: {
        latitude: {
            min: number;
            max: number;
        };
        longitude: {
            min: number;
            max: number;
        };
    };
    contact: {
        pattern: RegExp;
        message: string;
    };
    elevation: {
        min: number;
        max: number;
    };
}

// Sensor Management Types
export interface SensorType {
    token: string;
    name: string;
    category: 'environmental' | 'soil' | 'fuel' | 'system' | 'special';
    description: string;
    unit?: string;
}export interface SensorConfiguration {
    selectedSensors: string[];           // Internal working array
    sensorTokens: string[];              // → Maps to station.sensorTokens (space-separated string)
    sensorArray: string[];               // → Maps to station.sensorArray (array of tokens in transmission order)
    tokenString: string;                 // Comma-separated preview string
}

export interface SensorValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
}

export const AVAILABLE_SENSORS: SensorType[] = [
    // Environmental Sensors
    { token: 'temp', name: 'Temperature', category: 'environmental', description: 'Air temperature', unit: '°C' },
    { token: 'tmax', name: 'Hourly Max Temp', category: 'environmental', description: 'Hourly maximum temperature', unit: '°C' },
    { token: 'tmax24', name: '24h Max Temp', category: 'environmental', description: '24-hour maximum temperature', unit: '°C' },
    { token: 'tmin', name: 'Hourly Min Temp', category: 'environmental', description: 'Hourly minimum temperature', unit: '°C' },
    { token: 'tmin24', name: '24h Min Temp', category: 'environmental', description: '24-hour minimum temperature', unit: '°C' },
    { token: 'rh', name: 'Relative Humidity', category: 'environmental', description: 'Relative humidity', unit: '%' },
    { token: 'rhmax', name: 'Max Humidity', category: 'environmental', description: 'Maximum relative humidity', unit: '%' },
    { token: 'rhmax24', name: '24h Max Humidity', category: 'environmental', description: '24-hour maximum humidity', unit: '%' },
    { token: 'rhmin', name: 'Min Humidity', category: 'environmental', description: 'Minimum relative humidity', unit: '%' },
    { token: 'rhmin24', name: '24h Min Humidity', category: 'environmental', description: '24-hour minimum humidity', unit: '%' },
    { token: 'wspd', name: 'Wind Speed', category: 'environmental', description: 'Wind speed', unit: 'km/h' },
    { token: 'dir', name: 'Wind Direction', category: 'environmental', description: 'Wind direction', unit: '°' },
    { token: 'gust', name: 'Wind Gust', category: 'environmental', description: 'Wind gust speed', unit: 'km/h' },
    { token: 'gustdir', name: 'Gust Direction', category: 'environmental', description: 'Wind gust direction', unit: '°' },
    { token: 'baro', name: 'Barometric Pressure', category: 'environmental', description: 'Atmospheric pressure', unit: 'kPa' },
    { token: 'rain', name: 'Rainfall', category: 'environmental', description: 'Precipitation', unit: 'mm' },
    { token: 'raintotal', name: 'Total Rainfall', category: 'environmental', description: 'Total precipitation', unit: 'mm' },
    { token: 'rn24', name: '24h Rainfall', category: 'environmental', description: '24-hour rainfall', unit: 'mm' },
    { token: 'snow', name: 'Snow Depth', category: 'environmental', description: 'Snow depth', unit: 'cm' },
    { token: 'pyr', name: 'Pyrometer', category: 'environmental', description: 'Solar radiation', unit: 'W/m²' },



    // Soil Sensors
    { token: 'soilt1', name: 'Soil Temp 1', category: 'soil', description: 'Soil temperature sensor 1', unit: '°C' },
    { token: 'soilt2', name: 'Soil Temp 2', category: 'soil', description: 'Soil temperature sensor 2', unit: '°C' },
    { token: 'soilt3', name: 'Soil Temp 3', category: 'soil', description: 'Soil temperature sensor 3', unit: '°C' },
    { token: 'soilt4', name: 'Soil Temp 4', category: 'soil', description: 'Soil temperature sensor 4', unit: '°C' },
    { token: 'soilt5', name: 'Soil Temp 5', category: 'soil', description: 'Soil temperature sensor 5', unit: '°C' },
    { token: 'soilt6', name: 'Soil Temp 6', category: 'soil', description: 'Soil temperature sensor 6', unit: '°C' },
    { token: 'soilt7', name: 'Soil Temp 7', category: 'soil', description: 'Soil temperature sensor 7', unit: '°C' },
    { token: 'soilt8', name: 'Soil Temp 8', category: 'soil', description: 'Soil temperature sensor 8', unit: '°C' },
    { token: 'soilt9', name: 'Soil Temp 9', category: 'soil', description: 'Soil temperature sensor 9', unit: '°C' },
    { token: 'soilt10', name: 'Soil Temp 10', category: 'soil', description: 'Soil temperature sensor 10', unit: '°C' },
    { token: 'soilt11', name: 'Soil Temp 11', category: 'soil', description: 'Soil temperature sensor 11', unit: '°C' },
    { token: 'soilt12', name: 'Soil Temp 12', category: 'soil', description: 'Soil temperature sensor 12', unit: '°C' },
    { token: 'soilt13', name: 'Soil Temp 13', category: 'soil', description: 'Soil temperature sensor 13', unit: '°C' },
    { token: 'soilt14', name: 'Soil Temp 14', category: 'soil', description: 'Soil temperature sensor 14', unit: '°C' },
    { token: 'soilt15', name: 'Soil Temp 15', category: 'soil', description: 'Soil temperature sensor 15', unit: '°C' },
    { token: 'soilt16', name: 'Soil Temp 16', category: 'soil', description: 'Soil temperature sensor 16', unit: '°C' },
    { token: 'soilt18', name: 'Soil Temp 18', category: 'soil', description: 'Soil temperature sensor 18', unit: '°C' },
    { token: 'soilt19', name: 'Soil Temp 19', category: 'soil', description: 'Soil temperature sensor 19', unit: '°C' },
    { token: 'soilt20', name: 'Soil Temp 20', category: 'soil', description: 'Soil temperature sensor 20', unit: '°C' },
    { token: 'soilt21', name: 'Soil Temp 21', category: 'soil', description: 'Soil temperature sensor 21', unit: '°C' },
    { token: 'soilt22', name: 'Soil Temp 22', category: 'soil', description: 'Soil temperature sensor 22', unit: '°C' },
    { token: 'soilt23', name: 'Soil Temp 23', category: 'soil', description: 'Soil temperature sensor 23', unit: '°C' },
    { token: 'soilt24', name: 'Soil Temp 24', category: 'soil', description: 'Soil temperature sensor 24', unit: '°C' },
    { token: 'soilt25', name: 'Soil Temp 25', category: 'soil', description: 'Soil temperature sensor 25', unit: '°C' },
    { token: 'soilt26', name: 'Soil Temp 26', category: 'soil', description: 'Soil temperature sensor 26', unit: '°C' },

    // Fuel Sensors
    { token: 'fstemp', name: 'Fuel Stick Temperature', category: 'fuel', description: 'Fuel stick temperature', unit: '°C' },
    { token: 'fsmoisture', name: 'Fuel Stick Moisture', category: 'fuel', description: 'Fuel stick moisture content', unit: '%' },

    // System/Power Sensors
    {
        token: 'vbat',
        name: 'Battery Voltage',
        category: 'system',
        description: 'The average battery voltage',
        unit: 'V'
    },
    {
        token: 'vb',
        name: 'Backup Battery',
        category: 'system',
        description: 'Backup battery voltage',
        unit: 'V'
    },
    {
        token: 'vslr',
        name: 'Solar Voltage',
        category: 'system',
        description: 'The average solar panel voltage, 0 common when dark',
        unit: 'V'
    },
    {
        token: 'vtx',
        name: 'Transmittion Voltage',
        category: 'system',
        description: 'The battery voltage during GOES transmission',
        unit: 'V'
    },
    {
        token: 'ibat',
        name: 'Battery Current',
        category: 'system',
        description: 'The average current from battery to logger, current draw is very low so 0 is common',
        unit: 'A'
    },
    {
        token: 'islr',
        name: 'Solar Panel Current',
        category: 'system',
        description: 'The average current from solar panel to batteries, commonly 0 when dark',
        unit: 'A'
    },
    {
        token: 'telem',
        name: 'Telemetry Voltage',
        category: 'system',
        description: 'The average battery voltage measured at GOES telemetry ',
        unit: 'V'
    },
    {
        token: 'swr',
        name: 'Standing wave ratio',
        category: 'system',
        description: 'Standing wave ratio',
        unit: ' to 1'
    },
    // Special/Placeholder
    { token: 'dummy', name: 'Dummy/Placeholder', category: 'special', description: 'Placeholder sensor (can be used multiple times)', unit: '' }
];

// CFFDRS Management Types
export interface CFFDRSStartParams {
  mode: 'spring' | 'summer' | 'off';
  date: string;        // ISO date string YYYY-MM-DD
  ffmc?: number;
  dmc?: number;
  dc?: number;
}

export interface CFFDRSMeta {
  lastStartMode?: string;
  lastStartDate?: string;
  lastStartFfmc?: number;
  lastStartDmc?: number;
  lastStartDc?: number;
  lastStartedBy?: string;
  lastStartedAt?: string;
}

export interface CFFDRSStatusResponse {
  cffdrsMode: 'spring' | 'summer' | 'off' | null;
  cffdrsMeta: CFFDRSMeta | null;
}